import { morphism } from 'morphism';
import { CategoryID } from '@sbt-web/network/types';

/**
 * Maps macro category IDs to their corresponding verticals.
 * @type {Record<MacroCategoryID, Vertical>}
 */
const macroToVerticalMap = {
    [CategoryID.Tutte]: 'subito',
    [CategoryID.Motori]: 'motors',
    [CategoryID.Immobili]: 'realestate',
    [CategoryID.LavoroServizi]: 'jobs',
    [CategoryID.Elettronica]: 'market',
    [CategoryID.CasaPersona]: 'market',
    [CategoryID.SportsHobby]: 'market',
    [CategoryID.Altri]: 'market',
};
/**
 * Special category representing "Tutte le categorie" (All categories).
 * This category does not exist in the backend but is used to generate the filters UI.
 */
const category0 = {
    identifier: '0',
    friendly_name: 'usato',
    label: 'Tutte le categorie',
    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
    default_ad_type: 's',
    ad_types: ['s', 'g', 'u', 'h', 'k'],
    categories: [],
};
/**
 * Categories generated from an Anubi response.
 * See: https://github.mpi-internal.com/SCM-Italy/SearchConfigurationResources
 */
const generatedCategories = {
    macrocategories: [
        {
            identifier: '1',
            friendly_name: 'motori',
            label: 'MOTORI',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'k'],
            categories: [
                {
                    identifier: '2',
                    friendly_name: 'auto',
                    label: 'Auto',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: [
                        'datedesc',
                        'relevance',
                        'priceasc',
                        'pricedesc',
                        'yearasc',
                        'yeardesc',
                        'mileageasc',
                        'mileagedesc',
                    ],
                },
                {
                    identifier: '5',
                    friendly_name: 'accessori-auto',
                    label: 'Accessori Auto',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '3',
                    friendly_name: 'moto-e-scooter',
                    label: 'Moto e Scooter',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: [
                        'datedesc',
                        'relevance',
                        'priceasc',
                        'pricedesc',
                        'yearasc',
                        'yeardesc',
                        'mileageasc',
                        'mileagedesc',
                    ],
                },
                {
                    identifier: '36',
                    friendly_name: 'accessori-moto',
                    label: 'Accessori Moto',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '22',
                    friendly_name: 'nautica',
                    label: 'Nautica',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '34',
                    friendly_name: 'caravan-e-camper',
                    label: 'Caravan e Camper',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '4',
                    friendly_name: 'veicoli-commerciali',
                    label: 'Veicoli commerciali',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
            ],
        },
        {
            identifier: '6',
            friendly_name: 'immobili',
            label: 'IMMOBILI',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'k', 'u', 'h'],
            categories: [
                {
                    identifier: '7',
                    friendly_name: 'appartamenti',
                    label: 'Appartamenti',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '43',
                    friendly_name: 'camere-posti-letto',
                    label: 'Camere/Posti letto',
                    default_ad_type: 'u',
                    ad_types: ['k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '29',
                    friendly_name: 'ville-singole-e-a-schiera',
                    label: 'Ville singole e a schiera',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '30',
                    friendly_name: 'terreni-e-rustici',
                    label: 'Terreni e rustici',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '31',
                    friendly_name: 'garage-e-box',
                    label: 'Garage e box',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '32',
                    friendly_name: 'loft-mansarde',
                    label: 'Loft, mansarde e altro',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '33',
                    friendly_name: 'case-vacanza',
                    label: 'Case vacanza',
                    default_ad_type: 'h',
                    ad_types: ['k', 'h'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '8',
                    friendly_name: 'uffici-locali-commerciali',
                    label: 'Uffici e Locali commerciali',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
            ],
        },
        {
            identifier: '24',
            friendly_name: 'lavoro',
            label: 'LAVORO E SERVIZI',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s'],
            categories: [
                {
                    identifier: '26',
                    friendly_name: 'offerte-lavoro',
                    label: 'Offerte di lavoro',
                    default_ad_type: 's',
                    ad_types: ['s'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '50',
                    friendly_name: 'servizi',
                    label: 'Servizi',
                    default_ad_type: 's',
                    ad_types: ['s'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '42',
                    friendly_name: 'cerco-lavoro',
                    label: 'Candidati in cerca di lavoro',
                    default_ad_type: 's',
                    ad_types: ['s'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '25',
                    friendly_name: 'attrezzature',
                    label: 'Attrezzature di lavoro',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
            ],
        },
        {
            identifier: '9',
            friendly_name: 'elettronica',
            label: 'ELETTRONICA',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'g', 'k'],
            categories: [
                {
                    identifier: '10',
                    friendly_name: 'informatica',
                    label: 'Informatica',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '44',
                    friendly_name: 'videogiochi',
                    label: 'Console e Videogiochi',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '11',
                    friendly_name: 'audio-video',
                    label: 'Audio/Video',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '40',
                    friendly_name: 'fotografia',
                    label: 'Fotografia',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '12',
                    friendly_name: 'telefonia',
                    label: 'Telefonia',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
            ],
        },
        {
            identifier: '13',
            friendly_name: 'casa-e-persona',
            label: 'PER LA CASA e LA PERSONA',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'g', 'k'],
            categories: [
                {
                    identifier: '14',
                    friendly_name: 'arredamento-casalinghi',
                    label: 'Arredamento e Casalinghi',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '37',
                    friendly_name: 'elettrodomestici',
                    label: 'Elettrodomestici',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '15',
                    friendly_name: 'giardino-fai-da-te',
                    label: 'Giardino e Fai da te',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '16',
                    friendly_name: 'abbigliamento-accessori',
                    label: 'Abbigliamento e Accessori',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '17',
                    friendly_name: 'bambini-giocattoli',
                    label: 'Tutto per i bambini',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
            ],
        },
        {
            identifier: '18',
            friendly_name: 'sport-hobby',
            label: 'SPORTS E HOBBY',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'g', 'k'],
            categories: [
                {
                    identifier: '23',
                    friendly_name: 'animali',
                    label: 'Animali',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '100',
                    friendly_name: 'accessori-per-animali',
                    label: 'Accessori per animali',
                    default_ad_type: 's',
                    ad_types: ['s', 'g'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '19',
                    friendly_name: 'musica-film',
                    label: 'Musica e Film',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '38',
                    friendly_name: 'libri-riviste',
                    label: 'Libri e Riviste',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '39',
                    friendly_name: 'strumenti-musicali',
                    label: 'Strumenti Musicali',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '20',
                    friendly_name: 'sport',
                    label: 'Sports',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '41',
                    friendly_name: 'biciclette',
                    label: 'Biciclette',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '21',
                    friendly_name: 'hobby-collezionismo',
                    label: 'Collezionismo',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
            ],
        },
        {
            identifier: '27',
            friendly_name: 'vari',
            label: '--',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'g', 'k'],
            categories: [
                {
                    identifier: '28',
                    friendly_name: 'annunci-vari',
                    label: 'Altri',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
            ],
        },
    ],
};
/**
 * Array containing all macro categories, including the special "Tutte le categorie".
 * @type {Array}
 */
const allCategories = [category0, ...generatedCategories.macrocategories];
/**
 * Morphism schema for mapping a backend leaf category to a LeafCategory type.
 * @type {StrictSchema<LeafCategory>}
 */
const categorySchema = {
    id: 'identifier',
    label: 'label',
    friendly: 'friendly_name',
    orders: 'sorting_list',
    adTypes: 'ad_types',
    defaultAdType: 'default_ad_type',
    zoneSupport: 'supports_city_zones',
};
/**
 * Morphism schema for mapping a backend macro category to a MacroCategory type.
 * @type {StrictSchema<MacroCategory>}
 */
const macroCategorySchema = {
    id: 'identifier',
    label: 'label',
    friendly: 'friendly_name',
    orders: 'sorting_list',
    adTypes: 'ad_types',
    defaultAdType: 'default_ad_type',
    categories: {
        path: 'categories',
        fn: morphism(categorySchema),
    },
};
/**
 * Special MacroCategory representing "Tutte le categorie".
 * @type {MacroCategory}
 */
const CategoryAll = morphism(macroCategorySchema, category0);
/**
 * The main store of all macro categories, each with their leaf categories.
 * @type {MacroCategory[]}
 */
const CategoryStore = allCategories.map(morphism(macroCategorySchema));
/**
 * Type-guard to identify if a Category is a MacroCategory.
 * @param cat - The category to check.
 * @returns True if the category is a macro category, false otherwise.
 * @example
 * ```ts
 * import { isMacroCategory } from '@sbt-web/category-utils';
 *
 * // Check if a category is a macro category
 * const cat = getCategoryByID(CategoryID.Auto);
 * console.log(isMacroCategory(cat)); // true
 * ```
 */
const isMacroCategory = (cat) => CategoryStore.some((c) => c.id === cat.id);
/**
 * Type-guard to identify if a Category is a LeafCategory.
 * @param cat - The category to check.
 * @returns True if the category is a leaf category, false otherwise.
 *
 * @example
 * ```ts
 * import { isLeafCategory } from '@sbt-web/category-utils';
 *
 * // Check if a category is a leaf category
 * const cat = getCategoryByID(CategoryID.Auto);
 * console.log(isLeafCategory(cat)); // true
 * ```
 */
const isLeafCategory = (cat) => CategoryStore.some((macro) => macro.categories.some((c) => c.id === cat.id));
/**
 * Returns an array of all macro categories.
 * @returns An array containing all macro categories.
 * @example
 * ```ts
 * import { getAllMacroCategories } from '@sbt-web/category-utils';
 *
 * // Get all macro categories
 * const macroCategories = getAllMacroCategories();
 * console.log(macroCategories);
 * ```
 */
const getAllMacroCategories = () => CategoryStore;
/**
 * Returns a flat array of all macro and leaf categories.
 * @returns {Categories} An array containing all categories (macro and leaf).
 * @example
 * ```ts
 * import { getAllCategories } from '@sbt-web/category-utils';
 *
 * // Get a flat list of all macro and leaf categories
 * const categories = getAllCategories();
 *
 * // Print the names and IDs of all categories
 * categories.forEach(category => {
 *   console.log(`${category.label} (ID: ${category.id})`); // Auto (ID: 2)
 * });
 * ```
 */
const getAllCategories = () => CategoryStore.reduce((list, cat) => list.concat(cat).concat(cat.categories), []);
/**
 * Finds a category by its ID.
 * @param id - The ID of a category.
 * @returns The category corresponding to the ID, can be either a Macro or Leaf category, otherwise returns CategoryAll ("Tutte le categorie").
 *
 * @example
 * ```ts
 * import { getCategoryByID } from '@sbt-web/category-utils';
 *
 * // Get a category by its ID
 * const cat = getCategoryByID(CategoryID.Auto);
 * console.log(cat);
 * // { id: 2, label: 'Auto', friendly: 'auto', orders: ['datedesc', 'relevance', 'priceasc', 'pricedesc', 'yearasc', 'yeardesc', 'mileageasc', 'mileagedesc'], adTypes: ['s', 'k'], defaultAdType: 's', zoneSupport: false }
 * ```
 */
const getCategoryByID = (id) => {
    for (const macro of CategoryStore) {
        if (macro.id === id)
            return macro;
        for (const leaf of macro.categories) {
            if (leaf.id === id)
                return leaf;
        }
    }
    return CategoryAll;
};
/**
 * Finds the macro category of a generic category.
 * @param id - The ID to get the MacroCategory from.
 * @returns The MacroCategory of the current category, which might be itself, otherwise returns CategoryAll ("Tutte le categorie").
 *
 * @example
 * ```ts
 * import { getMacroOfCategoryID } from '@sbt-web/category-utils';
 *
 * // Get the macro category of a category
 * const macro = getMacroOfCategoryID(CategoryID.Auto);
 * console.log(macro);
 * // { id: 1, label: 'MOTORI', friendly: 'motori', orders: ['datedesc', 'priceasc', 'pricedesc'], adTypes: ['s', 'k'], defaultAdType: 's', categories: [Category, Category, Category, ...] }
 * ```
 */
const getMacroOfCategoryID = (id) => {
    for (const macro of CategoryStore) {
        if (macro.id === id)
            return macro;
        for (const leaf of macro.categories) {
            if (leaf.id === id)
                return macro;
        }
    }
    return CategoryAll;
};
/**
 * Finds the vertical of a generic category.
 * @param id - The ID to get the Vertical from.
 * @returns The Vertical of the current category, otherwise returns "subito" as default.
 *
 * @example
 * ```ts
 * import { getVerticalOfCategoryID } from '@sbt-web/category-utils';
 *
 * // Get the vertical of a category
 * const vertical = getVerticalOfCategoryID(CategoryID.Auto);
 * console.log(vertical); // 'motors'
 * ```
 */
const getVerticalOfCategoryID = (id) => macroToVerticalMap[getMacroOfCategoryID(id).id];
/**
 * Finds the shop label of a generic category.
 * @param id - The ID to get the Shop Label from.
 * @returns The Shop Label of the current category, otherwise returns "rivenditore" as default.
 *
 * @example
 * ```ts
 * import { getShopLabelOfCategoryID } from '@sbt-web/category-utils';
 *
 * // Get the shop label of a category
 * const label = getShopLabelOfCategoryID(CategoryID.OfferteLavoro);
 * console.log(label); // 'Azienda'
 * ```
 */
const getShopLabelOfCategoryID = (id) => {
    switch (id) {
        case CategoryID.Appartamenti:
        case CategoryID.CamerePostiLetto:
        case CategoryID.Ville:
        case CategoryID.TerreniRustici:
        case CategoryID.GarageBox:
        case CategoryID.LoftMansarde:
        case CategoryID.CaseVacanza:
        case CategoryID.UfficiLocali:
            return 'Agenzia';
        case CategoryID.OfferteLavoro:
            return 'Azienda';
        case CategoryID.Servizi:
        case CategoryID.Candidati:
        case CategoryID.Attrezzature:
            return '';
        default:
            return 'Rivenditore';
    }
};
/**
 * Finds a category by its friendly name.
 * @param name - The friendly name of the macro or leaf category to search.
 * @returns The category corresponding to the friendly name, can be either a Macro or Leaf category, otherwise returns CategoryAll ("Tutte le categorie").
 *
 * @example
 * ```ts
 * import { getCategoryByFriendly } from '@sbt-web/category-utils';
 *
 * // Get a category by its friendly name
 * const cat = getCategoryByFriendly('auto');
 * console.log(cat);
 * // { id: 2, label: 'Auto', friendly: 'auto', orders: ['datedesc', 'relevance', 'priceasc', 'pricedesc', 'yearasc', 'yeardesc', 'mileageasc', 'mileagedesc'], adTypes: ['s', 'k'], defaultAdType: 's', zoneSupport: false }
 * ```
 */
const getCategoryByFriendly = (name) => {
    for (const macro of CategoryStore) {
        if (macro.friendly === name)
            return macro;
        for (const leaf of macro.categories) {
            if (leaf.friendly === name)
                return leaf;
        }
    }
    return CategoryAll;
};
/**
 * Finds the label of a category by its ID.
 * @param id - The ID of a category.
 * @returns The label corresponding to the ID, if not found returns "Tutte le categorie".
 *
 * @example
 * ```ts
 * import { getCategoryLabelByID } from '@sbt-web/category-utils';
 *
 * // Get the label of a category
 * const label = getCategoryLabelByID(CategoryID.Auto);
 * console.log(label); // 'Auto'
 * ```
 */
const getCategoryLabelByID = (id) => getCategoryByID(id).label;
/**
 * Finds the friendly name of a category by its ID.
 * @param id - The ID of a category.
 * @returns The friendly name corresponding to the ID, if not found returns "usato".
 *
 * @example
 * ```ts
 * import { getCategoryFriendlyNameByID } from '@sbt-web/category-utils';
 *
 * // Get the friendly name of a category
 * const friendlyName = getCategoryFriendlyNameByID(CategoryID.Auto);
 * console.log(friendlyName); // 'auto'
 * ```
 */
const getCategoryFriendlyNameByID = (id) => getCategoryByID(id).friendly;
/**
 * List of macro category IDs that are considered "market" categories (shippable).
 * @type {CategoryID[]}
 */
const marketMacrocategories = [
    CategoryID.Elettronica,
    CategoryID.CasaPersona,
    CategoryID.SportsHobby,
    CategoryID.Altri,
];
/**
 * Determines if a category is shippable.
 * @param catID - The ID of the category to check.
 * @returns True if the category is shippable, false otherwise.
 *
 * @example
 * ```ts
 * import { isShippableCategory } from '@sbt-web/category-utils';
 *
 * // Check if a category is shippable
 * const isShippable = isShippableCategory(CategoryID.Auto);
 * console.log(isShippable); // true
 * ```
 */
const isShippableCategory = (catID) => {
    const id = catID;
    if (id === CategoryID.Animali) {
        return false;
    }
    else if (id === CategoryID.Tutte ||
        id === CategoryID.AccessoriAuto ||
        id === CategoryID.AccessoriMoto) {
        return true;
    }
    else if (isMacroCategory(getCategoryByID(id))) {
        return marketMacrocategories.includes(id);
    }
    else {
        return marketMacrocategories.includes(getMacroOfCategoryID(id).id);
    }
};

export { CategoryAll, CategoryStore, getAllCategories, getAllMacroCategories, getCategoryByFriendly, getCategoryByID, getCategoryFriendlyNameByID, getCategoryLabelByID, getMacroOfCategoryID, getShopLabelOfCategoryID, getVerticalOfCategoryID, isLeafCategory, isMacroCategory, isShippableCategory, macroToVerticalMap };
//# sourceMappingURL=index.js.map
